import React from "react";

/**
 * YouTube-accurate linkifier:
 * - ONLY linkifies absolute http/https URLs
 * - Leaves emails, bare domains, @handles as plain text
 * - Preserves newlines, trims trailing punctuation from links
 */
const YT_URL_RE = /(https?:\/\/[^\s<>"'()`]+)/gi;

function trimTrailingPunct(url: string): string {
  const punct = [".", ",", "!", "?", ":", ";"];
  let trimmed = url;
  
  // Strip common trailing punctuation
  while (punct.includes(trimmed.slice(-1))) {
    trimmed = trimmed.slice(0, -1);
  }
  
  // Handle unmatched closing parenthesis
  if (trimmed.endsWith(")")) {
    const openCount = (trimmed.match(/\(/g) || []).length;
    const closeCount = (trimmed.match(/\)/g) || []).length;
    if (closeCount > openCount) {
      trimmed = trimmed.slice(0, -1);
    }
  }
  
  return trimmed;
}

export function renderLinkifiedTextYouTubeStrict(text: string): React.ReactNode {
  if (!text) return null;

  const lines = text.split(/\r?\n/);
  
  return (
    <>
      {lines.map((line, lineIndex) => {
        const nodes: React.ReactNode[] = [];
        let lastIndex = 0;

        line.replace(YT_URL_RE, (match, _group1, offset: number) => {
          // Add text before the match
          if (offset > lastIndex) {
            nodes.push(line.slice(lastIndex, offset));
          }

          const trimmed = trimTrailingPunct(match);
          const overshoot = match.length - trimmed.length;

          // Check if it's an Instagram URL and format as shorthand
          let displayText = trimmed;
          const igMatch = trimmed.match(/https:\/\/instagram\.com\/([a-zA-Z0-9_.]+)/);
          if (igMatch) {
            displayText = `📷 / ${igMatch[1]}`;
          }

          // Add the link
          nodes.push(
            <a
              key={`link-${lineIndex}-${offset}`}
              href={trimmed}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-700 underline"
            >
              {displayText}
            </a>
          );

          // Add any trimmed punctuation as plain text
          if (overshoot > 0) {
            nodes.push(match.slice(-overshoot));
          }

          lastIndex = offset + match.length;
          return match;
        });

        // Add remaining text after last match
        if (lastIndex < line.length) {
          nodes.push(line.slice(lastIndex));
        }

        return <div key={`line-${lineIndex}`}>{nodes}</div>;
      })}
    </>
  );
}

/**
 * Instagram handle helpers
 */

export function normalizeInstagramHandle(raw: string | null): string | null {
  if (!raw || typeof raw !== 'string') return null;
  
  let handle = raw.trim();
  
  // Handle full Instagram URLs
  const urlMatch = handle.match(/(?:https?:\/\/)?(?:www\.)?instagram\.com\/([a-zA-Z0-9_.]+)/);
  if (urlMatch) {
    handle = urlMatch[1];
  }
  
  // Remove @ prefix if present
  handle = handle.replace(/^@+/, '');
  
  // Treat empty, just dashes, or just underscores as invalid
  if (!handle || /^[-_]+$/.test(handle)) return null;
  
  // Validate: letters, numbers, dots, underscores only, 1-30 chars
  if (!/^[a-zA-Z0-9_.]{1,30}$/.test(handle)) return null;
  
  return handle;
}

export function instagramUrlFrom(raw: string | null): string {
  const handle = normalizeInstagramHandle(raw);
  return handle ? `https://instagram.com/${handle}` : '';
}

/**
 * Format Instagram handle for display (shorthand with emoji/symbol)
 */
export function formatInstagramDisplay(raw: string | null): string {
  const handle = normalizeInstagramHandle(raw);
  return handle ? `📷 / ${handle}` : '';
}